/**
 * Tiny self-rolled SVG sparkline for the 7-day status-change count.
 * We deliberately don't pull in a chart library — the dataset is at
 * most ~7-30 points and the visual is purely indicative.
 *
 * The path is drawn from the first bucket (oldest) to the last (today).
 * Each bucket renders a faint vertical guide so the reader can roughly
 * tell which day a peak corresponds to without a full axis.
 */
import type { ActivityBucket } from '../../lib/dashboard';

export interface ActivitySparklineProps {
  buckets: readonly ActivityBucket[];
  /** Pixel width / height for the inline SVG. Defaults to 240 × 56. */
  width?: number;
  height?: number;
}

export function ActivitySparkline({
  buckets,
  width = 240,
  height = 56,
}: ActivitySparklineProps): JSX.Element {
  if (buckets.length === 0) {
    return (
      <div className="text-xs text-slate-500">アクティビティがありません</div>
    );
  }
  const max = Math.max(1, ...buckets.map((b) => b.count));
  const pad = 4;
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;
  const stepX = buckets.length > 1 ? innerW / (buckets.length - 1) : 0;
  const points = buckets.map((b, i) => {
    const x = pad + stepX * i;
    const y = pad + innerH - (b.count / max) * innerH;
    return { x, y, bucket: b };
  });
  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(' ');
  const areaPath = `${linePath} L ${points[points.length - 1]!.x.toFixed(1)} ${(pad + innerH).toFixed(1)} L ${points[0]!.x.toFixed(1)} ${(pad + innerH).toFixed(1)} Z`;
  const totalCount = buckets.reduce((sum, b) => sum + b.count, 0);

  return (
    <div className="flex flex-col gap-1" data-testid="activity-sparkline">
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="overflow-visible"
        role="img"
        aria-label={`直近 ${buckets.length} 日のアクティビティ: 合計 ${totalCount} 件`}
      >
        <path d={areaPath} fill="rgb(59 130 246 / 0.15)" />
        <path
          d={linePath}
          fill="none"
          stroke="rgb(59 130 246)"
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {points.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={2}
            fill="rgb(59 130 246)"
          >
            <title>
              {`${p.bucket.day.getMonth() + 1}/${p.bucket.day.getDate()} — ${p.bucket.count} 件`}
            </title>
          </circle>
        ))}
      </svg>
      <div className="flex justify-between text-[10px] text-slate-500 tabular-nums">
        <span>
          {`${buckets[0]!.day.getMonth() + 1}/${buckets[0]!.day.getDate()}`}
        </span>
        <span>合計 {totalCount} 件</span>
        <span>
          {`${buckets[buckets.length - 1]!.day.getMonth() + 1}/${buckets[buckets.length - 1]!.day.getDate()}`}
        </span>
      </div>
    </div>
  );
}
